/**
 * 대화 열 — 검색 결과 탭 바 아래, 결과 왼쪽에 붙는 접이식 "이 물품에 대해 더 묻기".
 * 2026-09-14 사용자 결정: 카드 안에 있던 대화 패널을 밖으로 빼서 결과를 보면서 LLM과 대화하고,
 * 답한 사실이 지도에 반영되는 흐름을 한 화면에서 닫는다. 열림 380px · 접힘 34px 띠(검색어·답 수·사실 반영 점).
 *
 * 문맥은 카드가 `askContextStore`로 게시한 것을 쓰고(없으면 검색어만), 채우기 칩은 카드와 같은 저장소
 * (`treeFactStore`)에 써서 카드가 재계산한다. 답 속 세번은 `requestAskFlash`로 카드의 마디를 켠다.
 * 답은 제안이며 판정에 들어가지 않는다 — 문구는 AskPanel이 그대로 단다.
 */
import React, { useCallback, useEffect, useState } from "react"; // eslint-disable-line no-unused-vars
import AskPanel from "./AskPanel";
import { ASK_MODELS, fetchUsage, readAskModel, writeAskModel } from "./branchMapClient";
import { requestAskFind, requestAskFlash, useAskContext } from "./askContextStore";
import { loadTreeFacts, saveTreeFacts } from "./treeFactStore";

const MUTED = "var(--audit-text-muted)"; const LINE = "var(--audit-border)"; const LINE_STRONG = "var(--audit-border-strong, #cdd1d8)";
const ELEV = "var(--audit-bg-elevated, var(--audit-bg))"; const BLUE = "#1d4ed8"; const BLUE_BG = "#eff6ff"; const BLUE_BG2 = "#dbeafe";

export const DRAWER_OPEN_WIDTH = 380;
export const DRAWER_RAIL_WIDTH = 34;
export const ASK_DRAWER_SOURCE = "ask-drawer";

export default function AskDrawer({ q, open, onToggle, fetchImpl, top = 52, width = DRAWER_OPEN_WIDTH, height = null }) {
  const ctx = useAskContext(q);
  const [answerCount, setAnswerCount] = useState(0);
  const [factTouched, setFactTouched] = useState(false);
  const facts = ctx?.facts || {};
  const onAnswer = useCallback((key, value) => {
    if (!key) return;
    const merged = { ...loadTreeFacts(q) };
    if (value === null || value === undefined) delete merged[key]; else merged[key] = value;
    saveTreeFacts(q, merged, ASK_DRAWER_SOURCE);
    setFactTouched(true);
  }, [q]);
  const onFlash = useCallback((code) => requestAskFlash(q, code), [q]);
  const onFind = useCallback((term) => requestAskFind(q, term), [q]);
  // 모델 선택(기본 Gemini 3.8 Flash · Opus 5)과 호출 집계 — 어느 모델로 묻는지 명시하고, 이번 달 호출 수를 센다.
  const [model, setModel] = useState(readAskModel);
  const pickModel = useCallback((key) => { setModel(key); writeAskModel(key); }, []);
  const [usage, setUsage] = useState(null);
  const [usageTick, setUsageTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    fetchUsage({ signal: ctrl.signal, fetchImpl }).then((u) => { if (!ctrl.signal.aborted) setUsage(u); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usageTick]);
  const ctxLine = ctx
    ? `${ctx.liveCodes?.length ? `살아 있는 후보 ${ctx.liveCodes.length}` : "후보 없음"} · 답한 사실 ${Object.keys(facts).length} · 채울 수 있는 질문 ${ctx.questionKeys?.length || 0}`
    : "사전 점검 카드가 아직 문맥을 주지 않았습니다 · 검색어만으로 묻습니다";

  if (!open) {
    return (
      <aside data-testid="ask-drawer" data-open="false" aria-label="이 물품에 대해 더 묻기 (접힘)"
        style={{ position: "sticky", top, alignSelf: "start", width: DRAWER_RAIL_WIDTH, minHeight: 220, maxHeight: `calc(100vh - ${top + 24}px)`, border: `1px solid ${LINE}`, borderRadius: 8, background: ELEV, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "8px 0", overflow: "hidden" }}>
        <button type="button" data-testid="ask-drawer-open" onClick={() => onToggle(true)} title="대화 열 열기" aria-label="대화 열 열기"
          style={{ border: "1px solid #2563eb", background: BLUE_BG, color: BLUE, borderRadius: 6, width: 24, height: 24, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, flex: "none" }}>›</button>
        <span style={{ writingMode: "vertical-rl", textOrientation: "mixed", fontSize: 11, color: MUTED, letterSpacing: ".04em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxHeight: 320 }}>
          {q} · 더 묻기{answerCount ? ` · 답 ${answerCount}` : ""}{Object.keys(facts).length ? ` · 사실 ${Object.keys(facts).length}` : ""}
        </span>
        {(factTouched || Object.keys(facts).length > 0) && <span aria-hidden="true" title="답한 사실이 지도에 반영됨" style={{ width: 8, height: 8, borderRadius: "50%", background: "#2563eb", boxShadow: `0 0 0 3px ${BLUE_BG2}`, marginTop: "auto", marginBottom: 6, flex: "none" }} />}
      </aside>
    );
  }
  return (
    <aside data-testid="ask-drawer" data-open="true" aria-label="이 물품에 대해 더 묻기"
      style={{ position: "sticky", top, alignSelf: "start", width, height: height ? height : `calc(100vh - ${top + 88}px)`, minHeight: 240, border: `1px solid ${LINE}`, borderRadius: 8, background: ELEV, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 6px 6px 8px", borderBottom: `1px solid ${LINE}` }}>
        <div style={{ minWidth: 0 }}>
          <b style={{ fontSize: 13, display: "block" }} title="답 속 세번을 누르면 지도의 그 마디가 켜지고, 채우기 칩은 기존 질문 키로 저장돼 지도가 다시 계산됩니다. 답은 제안이며 판정에 들어가지 않습니다.">이 물품에 대해 더 묻기</b>
          <div data-testid="ask-drawer-ctx" style={{ fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q} · {ctxLine}</div>
        </div>
        <button type="button" data-testid="ask-drawer-close" onClick={() => onToggle(false)} title="대화 열 접기" aria-label="대화 열 접기"
          style={{ marginLeft: "auto", flex: "none", border: `1px solid ${LINE_STRONG}`, background: "transparent", color: MUTED, borderRadius: 6, width: 26, height: 26, cursor: "pointer", fontFamily: "inherit" }}>‹</button>
      </div>
      <AskPanel
        layout="drawer"
        query={q}
        facts={facts}
        liveCodes={ctx?.liveCodes || []}
        liveNames={ctx?.liveNames || {}}
        hs4s={ctx?.hs4s || []}
        questionKeys={ctx?.questionKeys || []}
        narrowed={ctx?.narrowed || null}
        answers={facts}
        disabled={Boolean(ctx?.disabled)}
        onAnswer={onAnswer}
        onFlash={onFlash}
        onFind={onFind}
        onCount={setAnswerCount}
        onAnswered={() => setUsageTick((t) => t + 1)}
        model={model}
        fetchImpl={fetchImpl}
        bottomBarLeft={<UsageSpan usage={usage} />}
        bottomBarRight={<ModelMenu model={model} onPick={pickModel} />}
      />
    </aside>
  );
}

/** 하단 바 가운데 — 이번 달 호출 수·≈원(툴팁에 역할별 횟수·토큰·단가 근거). 숫자를 만들지 않는다: 단가 없으면 횟수만. */
function UsageSpan({ usage }) {
  const fmt = (n) => (n == null ? "" : Number(n).toLocaleString("ko-KR"));
  const m = usage?.month;
  // 2026-10-04: 기본 = gpt(오픈라우터) + gemini(이전), 정밀 = gpt-precise + opus(이전) — 역할로 합쳐 센다.
  const ROLE = { basic: ["gpt", "gemini"], precise: ["gpt-precise", "opus"] };
  const sum = (role, f) => ROLE[role].reduce((n, k) => n + (m?.models?.[k] ? f(m.models[k]) : 0), 0);
  const per = (role) => sum(role, (x) => x.calls);
  const tokens = (role) => `${fmt(sum(role, (x) => x.inTokens))}/${fmt(sum(role, (x) => x.outTokens))}`;
  const title = usage?.available
    ? `이번 달 · 기본 ${fmt(per("basic"))}회 · 정밀 ${fmt(per("precise"))}회 · 오늘 ${fmt(usage.today?.calls || 0)}회\n토큰(입력/출력) · 기본 ${tokens("basic")} · 정밀 ${tokens("precise")}${usage.priceNote ? `\n단가 · ${usage.priceNote}` : ""}`
    : "집계 표가 아직 없거나 로그인 전";
  return (
    <span data-testid="ask-usage" title={title} style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
      {usage?.available
        ? <>이번 달 <b style={{ color: "var(--audit-text)" }}>{fmt(per("basic") + per("precise"))}회</b>{usage.priced && m?.costKrw != null ? ` · ≈${fmt(m.costKrw)}원` : ""}{usage.budget ? ` / ${fmt(usage.budget.monthlyKrw)}원${usage.budget.over ? " 초과" : ""}` : ""}</>
        : "호출 집계 연결 전"}
    </span>
  );
}

/** 하단 바 오른쪽 — 답할 모델(역할 이름). 회사명은 툴팁에만. */
function ModelMenu({ model, onPick }) {
  const [open, setOpen] = useState(false);
  const current = ASK_MODELS.find((x) => x.key === model) || ASK_MODELS[0];
  return (
    <>
      <button type="button" data-testid="ask-model-current" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} title={["답할 모델", current.vendor, current.hint].filter(Boolean).join(" · ")}
        style={{ border: 0, background: "transparent", color: "var(--audit-text)", borderRadius: 6, padding: "3px 6px", fontFamily: "inherit", fontSize: 11, fontWeight: 600, cursor: "pointer", flex: "none", whiteSpace: "nowrap" }}>
        {current.label} <span aria-hidden="true" style={{ color: MUTED, fontSize: 10 }}>˅</span>
      </button>
      {open && (
        <div role="menu" aria-label="답할 모델" data-testid="ask-model-menu" style={{ position: "absolute", right: 6, bottom: 32, minWidth: 210, border: `1px solid ${LINE_STRONG}`, borderRadius: 8, background: ELEV, boxShadow: "0 6px 18px rgba(15,23,42,.12)", padding: 4, zIndex: 5 }}>
          {ASK_MODELS.map((x) => (
            <button type="button" role="menuitemradio" aria-checked={model === x.key} key={x.key} data-testid={`ask-model-${x.key}`} title={x.vendor} onClick={() => { onPick(x.key); setOpen(false); }}
              style={{ display: "flex", width: "100%", alignItems: "baseline", gap: 8, border: 0, borderRadius: 6, padding: "6px 9px", textAlign: "left", fontFamily: "inherit", fontSize: 12, cursor: "pointer", background: model === x.key ? BLUE_BG2 : "transparent", color: model === x.key ? BLUE : "var(--audit-text)" }}>
              <b style={{ minWidth: 96 }}>{x.label}</b><span style={{ fontSize: 11, color: MUTED }}>{x.hint}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
